import {useCAPIQuery} from './useCAPIQuery'
import {isPendingSession, type CommandsResponse} from '../types/session'
import {useSessionContext} from '../contexts/SessionContext'
import {fetchCAPI} from '../utils/fetch-capi'
import {useQueryClient} from '@github-ui/react-query'
import {useAgentSessionsAlive, type AgentSessionAlivePayload} from './use-agent-sessions-alive'
import {useCallback} from 'react'
import {useDebounce} from '@github-ui/use-debounce'

export interface UseListCommandsProps {
  pollingInterval: number
}

export function useListCommands({pollingInterval}: UseListCommandsProps) {
  const {session} = useSessionContext()
  const activeSessionId = session.id
  const queryClient = useQueryClient()
  const queryKey = ['agent-session-commands', activeSessionId]

  const handleAliveUpdate = useCallback(
    (payload?: AgentSessionAlivePayload) => {
      // Only invalidate commands for the active session
      if (payload?.session_id && payload.session_id !== activeSessionId) return
      queryClient.invalidateQueries({
        queryKey: ['agent-session-commands', activeSessionId],
      })
    },
    [queryClient, activeSessionId],
  )

  const debouncedHandleAliveUpdate = useDebounce(handleAliveUpdate, pollingInterval, {maxWait: 5000})

  // Subscribe to alive updates and get connection status for polling fallback
  const {isConnected: aliveIsConnected} = useAgentSessionsAlive({
    onEvent: debouncedHandleAliveUpdate,
    enableCreateEvents: false,
    enableUpdateEvents: true,
    enableLogUpdates: true,
  })

  // Only poll when session is active AND alive is not connected
  const shouldPoll =
    !aliveIsConnected && (session.state === 'in_progress' || session.state === 'queued') ? pollingInterval : false

  const query = useCAPIQuery<CommandsResponse>({
    queryKey,
    queryFn: async ({queryKey: currentQueryKey}) => {
      const result = await fetchCAPI({
        path: `/agents/sessions/${activeSessionId}/commands?sort=created_at,asc`,
        method: 'GET',
      })

      if (!result.ok) {
        throw new Error(`Failed to fetch commands: ${result.statusText}`)
      }

      const serverData = (await result.json()) as CommandsResponse

      // Get current cache data to preserve optimistic commands
      const currentData = queryClient.getQueryData<CommandsResponse>(currentQueryKey)

      if (currentData) {
        const optimisticCommands = currentData.commands.filter(cmd => cmd.id.startsWith('optimistic-'))

        // Merge server commands with optimistic commands (optimistic commands go at the end since they're the newest)
        return {
          commands: [...serverData.commands, ...optimisticCommands],
        }
      }

      return serverData
    },
    // Use polling as fallback when Alive is not connected and session is active
    refetchInterval: shouldPoll,
    enabled: !isPendingSession(session),
  })

  return {
    data: query.data?.commands ?? [],
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
  }
}
